/**
 * One renewal account — the detail behind a row on the Inspector list.
 *
 * ⚠️ THIS IS WHAT MAKES "OPEN" A REAL CONTROL. The list shipped without one
 * because the detail lived only on the standalone, and a button that goes
 * nowhere is worse than no button.
 *
 * ⚠️ UPLOADS GO THROUGH THE HUB'S API, NOT THE BROWSER'S STORAGE CLIENT. The
 * standalone uploads straight to Supabase Storage with its own session; there is
 * no such session here and creating one would mean a second sign-in against the
 * shared Entra registration. So the file is posted to the hub, which writes it
 * with the service role — at the same path shape the standalone reads, or the
 * file would upload cleanly here and fail to open over there.
 *
 * ⚠️ AND GENERATING THE WORKBOOK IS STILL ON THE STANDALONE. It is ~1,700 lines
 * of exceljs, pdfkit and prompt work; claiming it here with a button that opened
 * a new tab would be the same dishonesty the rail entry had. The page says where
 * it lives.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { StepBar } from '../components/StepBar';
import { PolicyComparison } from '../components/PolicyComparison';
import {
  ApiError,
  api,
  type InspectorAccount as Account,
  type InspectorCoverage,
  type InspectorDoc,
} from '../lib/api';

const STATUSES = [
  { key: 'intake', label: 'Intake' },
  { key: 'documents', label: 'Documents' },
  { key: 'analyzing', label: 'Analyzing' },
  { key: 'review', label: 'In Review' },
  { key: 'complete', label: 'Complete' },
];

/** The kinds the Inspector recognises. Anything else is not a kind it can read. */
const DOC_KINDS = [
  { kind: 'policy', label: 'Policy / Dec Page' },
  { kind: 'loss_run', label: 'Loss Run' },
  { kind: 'application', label: 'Application / ACORD' },
  { kind: 'financials', label: 'Financials' },
  { kind: 'supporting', label: 'Supporting Document' },
];

const KIND_LABEL: Record<string, string> = Object.fromEntries(DOC_KINDS.map((d) => [d.kind, d.label]));

/** High first. The order an agent should meet them in, not alphabetical. */
const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

const shortDate = (iso: string | number | null): string => {
  if (iso === null) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * One coverage with a tick-box, its definition, and — where we have one — why it
 * matters for this insured.
 *
 * ⚠️ THE DEFINITION IS SHOWN, NOT HIDDEN BEHIND A TOOLTIP (Josh, 2026-09-04: "I
 * also liked how they had definitions and reasoning for suggesting"). The agent
 * is deciding what to put in front of a client and then explaining it to them;
 * a name alone — "Ordinance or Law" — does not support either job, and the same
 * sentence is what prints in the packet's glossary.
 *
 * ⚠️ AND THE RATIONALE IS VISIBLY THE MODEL'S. It reads "Why for this insured",
 * set apart in italics, because it is generated prose about a specific business
 * and the agent is the one signing off on whether it is true.
 */
function CoverageRow({
  cov,
  category,
  checked,
  busy,
  priority,
  rationale,
  onToggle,
}: {
  cov: InspectorCoverage;
  category?: string;
  checked: boolean;
  busy: boolean;
  priority?: 'high' | 'medium' | 'low';
  rationale?: string;
  onToggle: () => void;
}) {
  return (
    <label className={`cov-row${checked ? ' is-on' : ''}`}>
      <input type="checkbox" checked={checked} disabled={busy} onChange={onToggle} />
      <div>
        <b>
          {cov.name}
          {priority && <span className={`tag is-${priority}`}>{priority}</span>}
          {category && <em>{category}</em>}
        </b>
        <span className="cov-def">{cov.definition}</span>
        {rationale && (
          <span className="cov-why">
            <i>Why for this insured:</i> {rationale}
          </span>
        )}
      </div>
    </label>
  );
}

const sizeOf = (bytes?: number): string =>
  !bytes ? '—' : bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`;

export function InspectorAccount(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState<Account | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [kind, setKind] = useState('policy');
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = () =>
    api
      .inspectorAccount(id)
      .then((r) => {
        setAccount(r.account);
        setErr('');
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not load that account.'));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Memoised so the sides useMemo below doesn't see a fresh [] every render.
  const docs: InspectorDoc[] = useMemo(() => account?.data?.documents ?? [], [account]);
  const artifacts = account?.data?.artifacts ?? [];
  /** Generation refuses without this — the server checks the same field. */
  const extracted = Boolean(account?.data?.extraction);

  /**
   * The account's sides, resolved the same way the server resolves them.
   *
   * ⚠️ AN ACCOUNT WITH NO `sides` IS ONE SIDE HOLDING EVERYTHING, not zero sides.
   * Six accounts already exist on the old flat shape and the standalone is still
   * creating more; rendering "no sides" for them would make a working account
   * look broken. Documents on no side fall to the primary one for the same
   * reason — a file that is visibly attached must not be silently unread.
   */
  const sides = useMemo(() => {
    const declared = account?.data?.sides ?? [];
    if (!declared.length) {
      return [{ id: 'primary', label: 'This account', documents: docs, isPrimary: true, extractedAt: undefined }];
    }
    const claimed = new Set(declared.flatMap((s) => s.documentIds ?? []));
    const orphans = docs.filter((d) => !claimed.has(d.id));
    const primaryIndex = Math.max(
      0,
      declared.findIndex((s) => s.id === 'primary'),
    );
    return declared.map((s, i) => {
      const own = (s.documentIds ?? [])
        .map((id) => docs.find((d) => d.id === id))
        .filter((d): d is InspectorDoc => Boolean(d));
      return {
        id: s.id,
        label: s.label,
        isPrimary: i === primaryIndex,
        documents: i === primaryIndex ? [...own, ...orphans] : own,
        extractedAt: s.extractedAt,
      };
    });
  }, [account, docs]);

  const hasSides = (account?.data?.sides ?? []).length > 0;

  /**
   * Is there anything to compare?
   *
   * ⚠️ TWO SIDES *READ*, NOT TWO SIDES DECLARED. Adding a side and dropping a
   * PDF on it takes ten seconds; reading it is the slow part. A comparison
   * panel that appeared as soon as a side existed would spend most of its life
   * showing one populated column and one empty one, which reads as a bug.
   *
   * The count is also the refresh key the panel watches, so finishing an
   * extraction rebuilds the comparison without a manual reload.
   */
  const readSides = sides.filter((s) => s.extractedAt || (s.isPrimary && extracted)).length;

  const recs = account?.data?.coverageRecs ?? [];
  const picked = account?.data?.recommendedSelected ?? [];

  /**
   * The catalogue, for names. Fetched once and tolerant of failure — a coverage
   * whose name we could not look up still shows its code rather than vanishing.
   */
  const [catalogue, setCatalogue] = useState<Map<string, InspectorCoverage>>(new Map());
  /** The specialty list, both grouped (to browse) and flat (to search). */
  const [groups, setGroups] = useState<{ key: string; label: string; items: InspectorCoverage[] }[]>([]);
  const [specialty, setSpecialty] = useState<(InspectorCoverage & { category: string })[]>([]);
  const [covQuery, setCovQuery] = useState('');
  const [showSpec, setShowSpec] = useState(false);
  useEffect(() => {
    api
      .inspectorCoverages()
      .then((r) => {
        const m = new Map<string, InspectorCoverage>();
        for (const c of r.gaps) m.set(c.code, c);
        const flat: (InspectorCoverage & { category: string })[] = [];
        for (const g of r.additional)
          for (const c of g.items) {
            m.set(c.code, c);
            flat.push({ ...c, category: g.label });
          }
        setCatalogue(m);
        setSpecialty(flat);
        setGroups(r.additional);
      })
      .catch(() => undefined);
  }, []);
  /** A coverage we could not look up still shows its code rather than vanishing. */
  const covOf = (code: string): InspectorCoverage =>
    catalogue.get(code) ?? { code, name: code, definition: '' };
  const nameOf = (code: string) => covOf(code).name;

  const extras = account?.data?.additionalSelected ?? [];
  const total = picked.length + extras.length;

  /**
   * ⚠️ SEARCHES THE DEFINITION TOO, NOT ONLY THE NAME. Half of these are known
   * by a word that is not in their title — "bobtail" is Non-Trucking Liability,
   * "boiler" is Equipment Breakdown. Matching names alone would hide the
   * coverage from the person who knows exactly what they want.
   */
  const covMatches = useMemo(() => {
    const q = covQuery.trim().toLowerCase();
    if (!q) return [];
    return specialty
      .filter((c) => `${c.name} ${c.definition} ${c.category}`.toLowerCase().includes(q))
      .slice(0, 25);
  }, [specialty, covQuery]);

  const toggleExtra = async (code: string) => {
    const next = extras.includes(code) ? extras.filter((c) => c !== code) : [...extras, code];
    setBusy('cov');
    setErr('');
    try {
      const out = await api.inspectorSetCoverages(id, { additionalSelected: next });
      setAccount(out.account);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy('');
    }
  };

  const recommend = async () => {
    setBusy('recs');
    setErr('');
    try {
      await api.inspectorRecommend(id);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not work out the gaps.');
    } finally {
      setBusy('');
    }
  };

  const togglePick = async (code: string) => {
    const next = picked.includes(code) ? picked.filter((c) => c !== code) : [...picked, code];
    setBusy('cov');
    setErr('');
    try {
      const out = await api.inspectorSetCoverages(id, { recommendedSelected: next });
      setAccount(out.account);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy('');
    }
  };

  const addSide = async (label: string) => {
    setBusy('side');
    setErr('');
    try {
      const out = await api.inspectorAddSide(id, label);
      setAccount(out.account);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not add the side.');
    } finally {
      setBusy('');
    }
  };

  const moveDoc = async (docId: string, sideId: string) => {
    setBusy(`move:${docId}`);
    setErr('');
    try {
      const out = await api.inspectorMoveDoc(id, docId, sideId);
      setAccount(out.account);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not move that document.');
    } finally {
      setBusy('');
    }
  };

  const extract = async (sideId = 'primary') => {
    setBusy(`extract:${sideId}`);
    setErr('');
    try {
      await api.inspectorExtract(id, sideId);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not read the documents.');
    } finally {
      setBusy('');
    }
  };

  const generate = async () => {
    setBusy('generate');
    setErr('');
    try {
      await api.inspectorGenerate(id);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not build the files.');
    } finally {
      setBusy('');
    }
  };

  /** Same two-minute signed link as documents, fetched on click rather than held. */
  const openArtifact = async (kind: string) => {
    setBusy(`art:${kind}`);
    try {
      const { url } = await api.inspectorArtifactUrl(id, kind);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open that file.');
    } finally {
      setBusy('');
    }
  };

  /**
   * ⚠️ OPENED VIA window.open ON A FRESH SIGNED URL, not an <a href> held on the
   * page. The link expires in two minutes; one rendered at page load would be
   * dead by the time anybody clicked it, and "the document is broken" is the
   * conclusion they would draw rather than "the link went stale".
   */
  const open = async (doc: InspectorDoc) => {
    setBusy(doc.id);
    try {
      const { url } = await api.inspectorDocUrl(id, doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open that document.');
    } finally {
      setBusy('');
    }
  };

  const upload = async (files: FileList | File[] | null) => {
    const list = files ? Array.from(files as ArrayLike<File>) : [];
    if (!list.length) return;
    setErr('');
    for (const file of list) {
      setBusy(`upload:${file.name}`);
      try {
        const contentBase64 = await new Promise<string>((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(',')[1] ?? '');
          r.onerror = () => rej(new Error('read failed'));
          r.readAsDataURL(file);
        });
        const out = await api.inspectorUploadDoc(id, {
          kind,
          fileName: file.name,
          mime: file.type || 'application/octet-stream',
          contentBase64,
        });
        setAccount(out.account);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : `Could not upload ${file.name}.`);
        // Stop on the first failure: the rest are likely to fail the same way,
        // and five identical errors say no more than one.
        break;
      } finally {
        setBusy('');
      }
    }
  };

  const setStatus = async (status: string) => {
    setBusy('status');
    try {
      const out = await api.inspectorSetStatus(id, status);
      setAccount(out.account);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change the status.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="wrap">
      <PageHero
        title={account?.named_insured || (account ? 'Unnamed account' : 'Account')}
        sub={account ? `${account.id} · started ${shortDate(account.created_at)}` : 'Loading…'}
        action={
          <button className="btn btn-ghost" type="button" onClick={() => navigate('/hub/inspector')}>
            All accounts
          </button>
        }
      />

      {err && <div className="empty">{err}</div>}

      <div className="pu-col">
        <section className="pu-panel">
          <div className="pu-head">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5" />
            </svg>
            <div>
              <h3>Where this account is</h3>
              <p>
                The stage drives what the Inspector does next with it. Uploading paperwork moves an
                account out of intake on its own.
              </p>
            </div>
          </div>
          {/* Clickable here, because staff genuinely move an account backwards —
              one marked complete that turns out to be missing a loss run has to
              go back. */}
          <StepBar
            steps={STATUSES}
            current={account?.status ?? ''}
            disabled={!account || busy === 'status'}
            onSelect={(key) => void setStatus(key)}
            label="Account stage"
          />
          {account && (
            <p className="supp-note" style={{ marginTop: 14 }}>
              {busy === 'status' ? 'Moving it…' : 'Click a step to move this account.'} · Effective{' '}
              {account.effective_date?.trim() || 'not given'}
            </p>
          )}
        </section>

        <section className="pu-panel">
          <div className="pu-head">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7zM14 3v4h4M12 17v-6M9.5 13.5L12 11l2.5 2.5" />
            </svg>
            <div>
              <h3>Documents</h3>
              <p>
                Policies, dec pages and loss runs. Say what a file is before you drop it — the
                Inspector reads a loss run differently from a dec page.
              </p>
            </div>
          </div>

          <div className="lr-filters" style={{ marginTop: 16 }}>
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Document kind">
              {DOC_KINDS.map((d) => (
                <option key={d.kind} value={d.kind}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          <label
            className={`pu-drop${dragging ? ' is-over' : ''}${busy.startsWith('upload:') ? ' is-busy' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void upload(e.dataTransfer.files);
            }}
          >
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(e) => void upload(e.target.files)}
            />
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.5 18a4.5 4.5 0 01-.6-8.96 6 6 0 0111.64-1.1A4.25 4.25 0 0118 18M12 20v-8M9 15l3-3 3 3" />
            </svg>
            <b>
              {busy.startsWith('upload:')
                ? `Uploading ${busy.slice(7)}…`
                : dragging
                  ? 'Drop it here'
                  : `Drag and drop ${KIND_LABEL[kind]?.toLowerCase() ?? 'files'} here`}
            </b>
            <span>Or click to browse your files</span>
            {/* The real ceiling, not a round number — see MAX_DOC_BYTES. */}
            <span className="pu-drop-types">PDF, images and spreadsheets up to 15 MB</span>
          </label>

          {/*
            ⚠️ GROUPED BY SIDE, AND EACH SIDE IS READ SEPARATELY. One list read
            into one extraction is what made comparison impossible: an expiring
            and a proposed policy merged with nothing recording which limit came
            from which. An account that has never had a second side shows exactly
            one group and reads as it always did.
          */}
          {sides.map((side) => (
            <div key={side.id} className="ins-side">
              <div className="ins-side-head">
                <b>{side.label}</b>
                <span>
                  {side.documents.length} file{side.documents.length === 1 ? '' : 's'}
                  {side.isPrimary && hasSides ? ' · drives the workbook' : ''}
                  {side.extractedAt ? ` · read ${shortDate(side.extractedAt)}` : ''}
                </span>
                {side.documents.length > 0 && (
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={busy.startsWith('extract') || busy === 'generate'}
                    onClick={() => void extract(side.id)}
                  >
                    {busy === `extract:${side.id}` ? 'Reading…' : side.extractedAt ? 'Re-read' : 'Read these'}
                  </button>
                )}
              </div>

              {side.documents.length === 0 ? (
                <p className="supp-note" style={{ margin: '10px 0 0' }}>
                  Nothing on this side yet — move a file onto it below, or upload with this side
                  selected.
                </p>
              ) : (
                <table className="lr-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Kind</th>
                      <th>Size</th>
                      {sides.length > 1 && <th>Side</th>}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {side.documents.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <b style={{ fontWeight: 600 }}>{d.fileName}</b>
                        </td>
                        <td>
                          <span className="tag">{KIND_LABEL[d.kind] ?? d.kind}</span>
                        </td>
                        <td>
                          <span className="sa-when">{sizeOf(d.size)}</span>
                        </td>
                        {sides.length > 1 && (
                          <td>
                            {/* Moving is a select rather than drag: it has to work
                                on a phone, and there are at most four targets. */}
                            <select
                              className="ins-move"
                              value={side.id}
                              disabled={busy === `move:${d.id}`}
                              aria-label={`Which side ${d.fileName} belongs to`}
                              onChange={(e) => void moveDoc(d.id, e.target.value)}
                            >
                              {sides.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        )}
                        <td className="sa-act">
                          <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={busy === d.id}
                            onClick={() => void open(d)}
                          >
                            {busy === d.id ? 'Opening…' : 'Open'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          {docs.length > 0 && sides.length < 4 && (
            <div className="supp-buttons" style={{ marginTop: 14 }}>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={busy === 'side'}
                onClick={() => void addSide(hasSides ? `Side ${sides.length + 1}` : 'Expiring')}
              >
                {busy === 'side' ? 'Adding…' : '+ Add a side to compare against'}
              </button>
            </div>
          )}

          {account && !docs.length && (
            <p className="supp-note" style={{ marginTop: 14 }}>
              Nothing attached yet. The analysis is built from these, so an account with no
              paperwork has nothing to read.
            </p>
          )}
        </section>

        {/*
          ⚠️ ABOVE THE ANALYSIS PANEL, NOT BELOW IT. The workbook and the client
          presentation are both built from the main side alone; the comparison is
          the only thing on this page that uses the second side at all. Putting
          it after the generate buttons would put the reason you added a side
          underneath the thing that ignores it.

          It renders nothing at all until two sides have been read — see the
          component. `readSides` is also the key it refreshes on.
        */}
        <PolicyComparison accountId={id} ready={readSides >= 2} />

        {/*
          The two steps that turn a pile of PDFs into something you would send a
          client. They are separate on purpose: reading the documents is the slow,
          expensive half, and rebuilding the files after changing a selection
          should not pay for it again.
        */}
        <section className="pu-panel">
          <div className="pu-head">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v6M12 3L9 6M12 3l3 3M5 13h14v8H5zM9 17h6" />
            </svg>
            <div>
              <h3>Analysis</h3>
              <p>
                Read each side&rsquo;s documents above, then build the exposure workbook and the
                client presentation. Both are built from the main side — the account&rsquo;s own
                policies — not from what you are comparing it against.
              </p>
            </div>
          </div>

          <div className="supp-buttons" style={{ marginTop: 18 }}>
            <button
              className="btn btn-primary"
              type="button"
              disabled={!extracted || busy.startsWith('extract') || busy === 'generate'}
              onClick={() => void generate()}
            >
              {busy === 'generate' ? 'Building…' : 'Build workbook & presentation'}
            </button>
          </div>

          <p className="supp-note" style={{ marginTop: 12 }}>
            {!docs.length
              ? 'Attach the policies and loss runs first — there is nothing to read yet.'
              : !extracted
                ? 'Reading takes up to a minute or two: the whole of each PDF goes to the model.'
                : busy === 'generate'
                  ? 'Computing loss ratios, writing the summary, and building both files.'
                  : 'Documents have been read. Building is quick from here.'}
          </p>

          {artifacts.length > 0 && (
            <table className="lr-table" style={{ marginTop: 18 }}>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Built</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {artifacts.map((a) => (
                  <tr key={a.kind}>
                    <td>
                      <b style={{ fontWeight: 600 }}>{a.fileName}</b>
                    </td>
                    <td>
                      <span className="sa-when">{shortDate(a.at)}</span>
                    </td>
                    <td className="sa-act">
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        disabled={busy === `art:${a.kind}`}
                        onClick={() => void openArtifact(a.kind)}
                      >
                        {busy === `art:${a.kind}` ? 'Opening…' : 'Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/*
          ⚠️ THIS PANEL IS WHY THE SIGNATURE PAGE HAS TICK-BOXES. buildRecommended
          reads recommendedSelected + additionalSelected; with both empty the PDF
          falls back to an acknowledgment page with no rows to sign, which is
          what Gourmet Xpress produced and looked like a broken generator rather
          than an account nobody had made selections on.
        */}
        <section className="pu-panel">
          <div className="pu-head">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 11l3 3 8-8M20 12v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1h9" />
            </svg>
            <div>
              <h3>Coverage gaps</h3>
              <p>
                What this insured does not appear to carry. Tick the ones you want put in front of
                them — each ticked coverage becomes a line on the acknowledgment page they sign.
              </p>
            </div>
          </div>

          <div className="supp-buttons" style={{ marginTop: 18 }}>
            <button
              className="btn"
              type="button"
              disabled={!extracted || busy === 'recs' || busy === 'generate'}
              onClick={() => void recommend()}
            >
              {busy === 'recs' ? 'Thinking…' : recs.length ? 'Reconsider the gaps' : 'Find coverage gaps'}
            </button>
          </div>

          {!extracted && (
            <p className="supp-note" style={{ marginTop: 12 }}>
              Read the documents first — the gaps are worked out from the lines the policies
              actually show.
            </p>
          )}

          {recs.length > 0 && (
            <>
              {/* Sorted by what the model thought mattered most — an agent
                  reading top to bottom meets the important ones first. */}
              <div className="ins-spec-list" style={{ marginTop: 18 }}>
                {[...recs]
                  .sort((a, b) => (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1))
                  .map((r) => (
                    <CoverageRow
                      key={r.code}
                      cov={covOf(r.code)}
                      checked={picked.includes(r.code)}
                      busy={busy === 'cov'}
                      priority={r.priority}
                      /* Absent when the model had nothing specific to say. An
                         invented sentence on a document a client signs is worse
                         than a missing one. */
                      rationale={r.rationale || undefined}
                      onToggle={() => void togglePick(r.code)}
                    />
                  ))}
              </div>
              <p className="supp-note" style={{ marginTop: 12 }}>
                {total
                  ? `${total} coverage${total === 1 ? '' : 's'} will appear on the acknowledgment page. Rebuild the files after changing this.`
                  : 'Nothing ticked — the acknowledgment page will have no coverages to sign for.'}
              </p>
            </>
          )}

          {/*
            ⚠️ THE SPECIALTY LIST IS SEARCHED, NOT SCROLLED. There are 137 of
            them across 14 categories; a nested set of collapsible lists is a
            thing nobody finds "Riggers Liability" in. Typing two letters is.
          */}
          {/*
            ⚠️ BROWSE BY CATEGORY *AND* SEARCH (Josh, 2026-09-04: "I liked the way
            you could select coverages in the stand alone version"). I built this
            search-only first, arguing nobody finds "Riggers Liability" by
            scanning — which is true when you know the name and wrong when you do
            not. Half the point of a gap review is being reminded of a coverage
            you had not thought of, and you cannot search for that. Categories
            are how you find it; the search is how you skip the categories.
          */}
          <div className="ins-spec">
            <div className="ins-spec-head">
              <b>Specialty &amp; additional coverages</b>
              <span>
                Less-common coverages from across every P&amp;C risk type — {specialty.length} of
                them. Anything ticked joins the packet with its definition, and picks up an AI
                &ldquo;why for this insured&rdquo; note next time you find gaps.
              </span>
            </div>

            <div className="supp-buttons" style={{ marginTop: 12 }}>
              <button className="btn btn-sm btn-ghost" type="button" onClick={() => setShowSpec((s) => !s)}>
                {showSpec ? 'Hide specialty coverages' : 'Browse specialty coverages'}
              </button>
            </div>

            {extras.length > 0 && (
              <div className="ins-chips" style={{ marginTop: 12 }}>
                {extras.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="ins-chip is-on"
                    disabled={busy === 'cov'}
                    title="Remove"
                    onClick={() => void toggleExtra(code)}
                  >
                    {nameOf(code)} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            )}

            {showSpec && (
              <>
                <div className="lr-search" style={{ margin: '12px 0' }}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
                  </svg>
                  <input
                    type="search"
                    value={covQuery}
                    onChange={(e) => setCovQuery(e.target.value)}
                    placeholder="Search all coverages — bobtail, boiler, liquor…"
                    aria-label="Search specialty coverages"
                  />
                </div>

                {covQuery.trim() ? (
                  covMatches.length === 0 ? (
                    <p className="supp-note">Nothing matches that.</p>
                  ) : (
                    <div className="ins-spec-list">
                      {covMatches.map((c) => (
                        <CoverageRow
                          key={c.code}
                          cov={c}
                          category={c.category}
                          checked={extras.includes(c.code)}
                          busy={busy === 'cov'}
                          onToggle={() => void toggleExtra(c.code)}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  <div className="ins-cats">
                    {groups.map((g) => {
                      const on = g.items.filter((i) => extras.includes(i.code)).length;
                      return (
                        <details key={g.key} className="ins-cat">
                          <summary>
                            {g.label}
                            {/* The count is on the closed summary on purpose: it is
                                the only way to see you have ticked something inside
                                a category without opening all fourteen. */}
                            {on > 0 && <span className="ins-cat-n">{on}</span>}
                          </summary>
                          <div className="ins-spec-list">
                            {g.items.map((c) => (
                              <CoverageRow
                                key={c.code}
                                cov={c}
                                checked={extras.includes(c.code)}
                                busy={busy === 'cov'}
                                onToggle={() => void toggleExtra(c.code)}
                              />
                            ))}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <section className="panel lr-about">
          <div>
            <h3>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
              </svg>
              What happens next
            </h3>
            <p>
              Once the policies and loss runs are attached, the Inspector reads them and builds an
              exposure workbook and an executive loss summary — the figures come off the documents
              rather than being re-keyed.
            </p>
            <p className="lr-about-note">
              This account is the same row the standalone Inspector shows. Anything you attach here
              is there too.
            </p>
          </div>
          <div>
            <h3>Still on the standalone</h3>
            <ul>
              <li>Generating the exposure workbook and loss summary</li>
              <li>The extracted policy and claim detail</li>
              <li>The client review screen</li>
              <li>Deleting a document once it is attached</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
